/**
 * Settings → Shipping (T11-41, design/04 §13, design/06 §8) — every call and its owner:
 *
 * | what | endpoint | permission |
 * |---|---|---|
 * | zones · one zone | orders `GET/POST /v1/admin/shipping-zones`, `GET/PATCH/DELETE …/{id}` (If-Match) | orders.read / settings.write |
 * | rates of a zone | orders `GET/POST /v1/admin/shipping-rates[?zoneId=]`, `PATCH/DELETE …/{id}` (If-Match) | orders.read / settings.write |
 * | carriers + tracking link | orders `GET /v1/admin/carriers`, `PATCH …/{code}` | orders.read / settings.write |
 * | "what would the customer see" | orders `POST /v1/admin/shipping/quote` (no bag, nothing reserved) | orders.read |
 * | packing the engine adds | orders `GET/PATCH /v1/admin/settings/shipping` (If-Match; next quote uses it — T05-27) | orders.read / settings.write |
 * | duties per market (DDU/DDP, %) | tax `GET /v1/admin/markets`, `PATCH …/{code}` | tax.read / tax.settings |
 * | 都道府県 names | orders `GET /v1/store/addresses/jp-prefectures?locale=` | — |
 * | item values for the calculator | catalog `GET /v1/admin/variants/lookup?code=` | products.read |
 *
 * Prices, tiers, insurance and duties are decided by order-service; the admin never picks a tier
 * or multiplies a duty rate itself.
 */
import {
  createCatalogClient,
  createOrdersClient,
  createTaxClient,
  type OrdersPaths,
  type ServiceClientOptions,
  type TaxPaths,
} from "@tcg/api-client";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { unwrap } from "@/lib/api/errors";
import { useClientOptions } from "@/lib/api/use-api";

type Json<T> = T extends { content: { "application/json": infer B } } ? B : never;
type Body<T> = T extends { requestBody: { content: { "application/json": infer B } } }
  ? B
  : T extends { requestBody?: { content: { "application/json": infer B } } }
    ? B
    : never;
type O = OrdersPaths;
type X = TaxPaths;

export type ShippingZone = Json<O["/v1/admin/shipping-zones/{zone_id}"]["get"]["responses"][200]>;
export type ZoneIn = Body<O["/v1/admin/shipping-zones"]["post"]>;
export type ZonePatchIn = Body<O["/v1/admin/shipping-zones/{zone_id}"]["patch"]>;
export type ShippingRate = Json<O["/v1/admin/shipping-rates/{rate_id}"]["get"]["responses"][200]>;
export type RateIn = Body<O["/v1/admin/shipping-rates"]["post"]>;
export type RatePatchIn = Body<O["/v1/admin/shipping-rates/{rate_id}"]["patch"]>;
export type RateType = ShippingRate["type"];
export type Carrier = Json<O["/v1/admin/carriers"]["get"]["responses"][200]>["data"][number];
export type CarrierPatchIn = Body<O["/v1/admin/carriers/{code}"]["patch"]>;
export type QuoteIn = Body<O["/v1/admin/shipping/quote"]["post"]>;
export type QuoteItemIn = QuoteIn["items"][number];
export type Quote = Json<O["/v1/admin/shipping/quote"]["post"]["responses"][200]>;
export type QuoteRate = Quote["rates"][number];
export type HiddenRate = Quote["hiddenRates"][number];
export type ShippingDefaults = Json<O["/v1/admin/settings/shipping"]["get"]["responses"][200]>;
export type ShippingDefaultsPatchIn = Body<O["/v1/admin/settings/shipping"]["patch"]>;
export type Prefecture = Json<
  O["/v1/store/addresses/jp-prefectures"]["get"]["responses"][200]
>["data"][number];
export type Market = Json<X["/v1/admin/markets/{code}"]["get"]["responses"][200]>;
export type MarketPatchIn = Body<X["/v1/admin/markets/{code}"]["patch"]>;

/** The values of one catalog item the calculator needs (price 税込, export price, weight). */
export interface LookedUpItem {
  title: string;
  sku: string;
  productType: QuoteItemIn["productType"];
  unitPrice: string;
  exportBasePrice: string | null;
  weightG: number | null;
}

export interface ShippingApi {
  zones(signal?: AbortSignal): Promise<ShippingZone[]>;
  createZone(body: ZoneIn): Promise<ShippingZone>;
  patchZone(id: string, body: ZonePatchIn, version: number): Promise<ShippingZone>;
  deleteZone(id: string, version: number): Promise<void>;
  rates(zoneId: string | null, signal?: AbortSignal): Promise<ShippingRate[]>;
  createRate(body: RateIn): Promise<ShippingRate>;
  patchRate(id: string, body: RatePatchIn, version: number): Promise<ShippingRate>;
  deleteRate(id: string, version: number): Promise<void>;
  carriers(signal?: AbortSignal): Promise<Carrier[]>;
  patchCarrier(code: string, body: CarrierPatchIn, version: number | null): Promise<Carrier>;
  quote(body: QuoteIn, locale: string, signal?: AbortSignal): Promise<Quote>;
  defaults(signal?: AbortSignal): Promise<ShippingDefaults>;
  patchDefaults(body: ShippingDefaultsPatchIn, version: number): Promise<ShippingDefaults>;
  prefectures(locale: string, signal?: AbortSignal): Promise<Prefecture[]>;
  markets(signal?: AbortSignal): Promise<Market[]>;
  patchMarket(code: string, body: MarketPatchIn): Promise<Market>;
  lookup(code: string, signal?: AbortSignal): Promise<LookedUpItem | null>;
}

const ifMatch = (version: number) => ({ "If-Match": `"${String(version)}"` });
const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});

const QUOTE_TYPES = new Set(["raw_single", "graded_single", "sealed_product", "supplies"]);

export function createShippingApi(options: ServiceClientOptions): ShippingApi {
  const orders = createOrdersClient(options);
  const tax = createTaxClient(options);
  const catalog = createCatalogClient(options);
  return {
    zones: async (signal) =>
      (await unwrap(orders.GET("/v1/admin/shipping-zones", withSignal(signal)))).data,
    createZone: (body) => unwrap(orders.POST("/v1/admin/shipping-zones", { body })),
    patchZone: (id, body, version) =>
      unwrap(
        orders.PATCH("/v1/admin/shipping-zones/{zone_id}", {
          params: { path: { zone_id: id }, header: ifMatch(version) },
          body,
        }),
      ),
    deleteZone: async (id, version) => {
      await unwrap(
        orders.DELETE("/v1/admin/shipping-zones/{zone_id}", {
          params: { path: { zone_id: id }, header: ifMatch(version) },
        }),
      );
    },
    rates: async (zoneId, signal) =>
      (
        await unwrap(
          orders.GET("/v1/admin/shipping-rates", {
            params: { query: { includeInactive: true, ...(zoneId ? { zoneId } : {}) } },
            ...withSignal(signal),
          }),
        )
      ).data,
    createRate: (body) => unwrap(orders.POST("/v1/admin/shipping-rates", { body })),
    patchRate: (id, body, version) =>
      unwrap(
        orders.PATCH("/v1/admin/shipping-rates/{rate_id}", {
          params: { path: { rate_id: id }, header: ifMatch(version) },
          body,
        }),
      ),
    deleteRate: async (id, version) => {
      await unwrap(
        orders.DELETE("/v1/admin/shipping-rates/{rate_id}", {
          params: { path: { rate_id: id }, header: ifMatch(version) },
        }),
      );
    },
    carriers: async (signal) =>
      (await unwrap(orders.GET("/v1/admin/carriers", withSignal(signal)))).data,
    patchCarrier: (code, body, version) =>
      unwrap(
        orders.PATCH("/v1/admin/carriers/{code}", {
          params: { path: { code }, ...(version !== null ? { header: ifMatch(version) } : {}) },
          body,
        }),
      ),
    quote: (body, locale, signal) =>
      unwrap(
        orders.POST("/v1/admin/shipping/quote", {
          params: { query: { locale } },
          body,
          ...withSignal(signal),
        }),
      ),
    defaults: (signal) => unwrap(orders.GET("/v1/admin/settings/shipping", withSignal(signal))),
    patchDefaults: (body, version) =>
      unwrap(
        orders.PATCH("/v1/admin/settings/shipping", {
          params: { header: ifMatch(version) },
          body,
        }),
      ),
    prefectures: async (locale, signal) =>
      (
        await unwrap(
          orders.GET("/v1/store/addresses/jp-prefectures", {
            params: { query: { locale } },
            ...withSignal(signal),
          }),
        )
      ).data,
    markets: async (signal) =>
      (await unwrap(tax.GET("/v1/admin/markets", withSignal(signal)))).data,
    patchMarket: (code, body) =>
      unwrap(tax.PATCH("/v1/admin/markets/{code}", { params: { path: { code } }, body })),
    lookup: async (code, signal) => {
      const hit = await unwrap(
        catalog.GET("/v1/admin/variants/lookup", {
          params: { query: { code } },
          ...withSignal(signal),
        }),
      ).catch((error: unknown) => {
        if ((error as { status?: number }).status === 404) {
          return null;
        }
        throw error;
      });
      if (!hit) {
        return null;
      }
      const { variant } = hit;
      const type = hit.product.productType;
      return {
        title: hit.product.title,
        sku: variant.sku,
        productType: (QUOTE_TYPES.has(type)
          ? type
          : "sealed_product") as LookedUpItem["productType"],
        unitPrice: variant.pricing.price.amount,
        exportBasePrice: variant.exportBasePrice.amount,
        weightG: variant.weightG > 0 ? variant.weightG : null,
      };
    },
  };
}

/**
 * Queries more than one tab uses (zones, rates, carriers, 都道府県) — one cache entry each;
 * `poll` = the interval while the live connection is down (`false` when it is up).
 */
export function useShippingData(language: string, poll: number | false) {
  const api = useShippingApi();
  const zones = useQuery({
    queryKey: ZONES_KEY,
    refetchInterval: poll,
    meta: { errorToast: false },
    queryFn: ({ signal }) => api.zones(signal),
  });
  const rates = useQuery({
    queryKey: RATES_KEY,
    refetchInterval: poll,
    meta: { errorToast: false },
    queryFn: ({ signal }) => api.rates(null, signal),
  });
  const carriers = useQuery({
    queryKey: CARRIERS_KEY,
    refetchInterval: poll,
    meta: { errorToast: false },
    queryFn: ({ signal }) => api.carriers(signal),
  });
  const prefectures = useQuery({
    queryKey: [...PREFECTURES_KEY, language],
    staleTime: 24 * 60 * 60 * 1000,
    meta: { errorToast: false },
    queryFn: ({ signal }) => api.prefectures(language, signal),
  });
  return { api, zones, rates, carriers, prefectures };
}

export function useShippingApi(): ShippingApi {
  const options = useClientOptions();
  return useMemo(() => createShippingApi(options), [options]);
}

export const SHIPPING_KEY = ["shipping"] as const;
export const ZONES_KEY = [...SHIPPING_KEY, "zones"] as const;
export const RATES_KEY = [...SHIPPING_KEY, "rates"] as const;
export const CARRIERS_KEY = [...SHIPPING_KEY, "carriers"] as const;
export const MARKETS_KEY = [...SHIPPING_KEY, "markets"] as const;
export const QUOTE_KEY = [...SHIPPING_KEY, "quote"] as const;
export const DEFAULTS_KEY = [...SHIPPING_KEY, "defaults"] as const;
export const PREFECTURES_KEY = [...SHIPPING_KEY, "prefectures"] as const;
